/**
 * Chiamate alle API del servizio `tools` (ADR 0008 §5.3).
 *
 * Connessioni dell'utente e dei progetti a GitHub e a Google, repository
 * visibili, flusso per dispositivo, progetti Google Cloud e loro panoramica
 * (ADR 0011 §5), collegamenti fra progetti e connessioni. Il token, o il JSON
 * di Google, viaggia solo verso il backend (creazione e rotazione) e nessuna
 * risposta lo porta.
 *
 * Provvisorio dichiarato: percorsi scritti a mano (CLAUDE.md §2.3), presidiati
 * da `tools.test.ts`.
 */

import type { ApiClient } from './client';
import type {
  GithubTransferRequest,
  GithubTransferResult,
  ManagedGithubRepository,
  ManagedGithubResponse,
} from './githubManagedTypes';
import type {
  GoogleProject,
  GoogleProjectList,
  GoogleProjectOverview,
  GoogleUsage,
  ManagedGoogleProject,
  ManagedGoogleResponse,
  MigrationRequest,
  MigrationResult,
} from './googleTypes';
import type {
  BindingChanges,
  ConnectionChanges,
  ConnectionOwnerRequest,
  DeviceFlowPoll,
  DeviceFlowStart,
  NewBinding,
  NewConnection,
  RepositoryPage,
  ToolBinding,
  ToolConnection,
  ToolProvider,
} from './toolTypes';

export type * from './toolTypes';
export type * from './googleTypes';
export type * from './githubManagedTypes';
export { GITHUB_OWNER_PATTERN } from './githubManagedTypes';
export { DEFAULT_BINDING_SETTINGS, bindingProvider, githubSettingsOf, googleProjectOf } from './toolTypes';
export { GOOGLE_METHODS } from './googleTypes';

/** Di chi leggere le connessioni. */
export type ConnectionsScope =
  | {
      /** Le mie, oppure quella di piattaforma (solo `ADMIN`). */
      owner: 'me' | 'platform';
    }
  | {
      /** Quelle di un progetto. */
      projectId: string;
    };

/** Prefisso comune delle rotte. */
const BASE = '/api/v1/tools';

/**
 * Percorso di una connessione.
 *
 * @param connectionId Connessione.
 * @returns Il percorso relativo al gateway.
 */
function connectionPath(connectionId: string): string {
  return `${BASE}/connections/${encodeURIComponent(connectionId)}`;
}

/**
 * Percorso del progetto Google gestito di un progetto institor.
 *
 * @param projectId Progetto institor.
 * @returns Il percorso relativo al gateway.
 */
function managedPath(projectId: string): string {
  return `${BASE}/projects/${encodeURIComponent(projectId)}/google/managed`;
}

/**
 * Percorso del repository GitHub gestito di un progetto (ADR 0014).
 *
 * @param projectId Progetto institor.
 * @returns Il percorso.
 */
function githubManagedPath(projectId: string): string {
  return `${BASE}/projects/${encodeURIComponent(projectId)}/github/managed`;
}

/** Operazioni del servizio `tools`, esposte alla UI. */
export class ToolsApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Elenca i fornitori e i metodi disponibili.
   *
   * @returns I fornitori.
   */
  listProviders(): Promise<ToolProvider[]> {
    return this.client.request<ToolProvider[]>('GET', `${BASE}/providers`);
  }

  /**
   * Elenca le connessioni mie o di un progetto.
   *
   * @param scope `{owner: 'me'}` oppure `{projectId}`.
   * @returns Le connessioni, senza token.
   */
  listConnections(scope: ConnectionsScope): Promise<ToolConnection[]> {
    const query =
      'owner' in scope ? `owner=${scope.owner}` : `project_id=${encodeURIComponent(scope.projectId)}`;
    return this.client.request<ToolConnection[]>('GET', `${BASE}/connections?${query}`);
  }

  /**
   * Crea una connessione con un token. Il backend lo valida presso GitHub.
   *
   * @param connection Fornitore, proprietario, metodo, token e nome.
   * @returns La connessione creata.
   */
  createConnection(connection: NewConnection): Promise<ToolConnection> {
    return this.client.request<ToolConnection>('POST', `${BASE}/connections`, connection);
  }

  /**
   * Rinomina una connessione o ne sostituisce il token.
   *
   * @param connectionId Connessione.
   * @param changes Nome e/o token nuovi.
   * @returns La connessione aggiornata.
   */
  updateConnection(connectionId: string, changes: ConnectionChanges): Promise<ToolConnection> {
    return this.client.request<ToolConnection>('PATCH', connectionPath(connectionId), changes);
  }

  /**
   * Rivalida una connessione presso il fornitore.
   *
   * @param connectionId Connessione.
   * @returns La connessione con stato e verifica aggiornati.
   */
  verifyConnection(connectionId: string): Promise<ToolConnection> {
    return this.client.request<ToolConnection>('POST', `${connectionPath(connectionId)}/verify`);
  }

  /**
   * Cancella una connessione, il suo segreto e i collegamenti che la usano.
   *
   * @param connectionId Connessione.
   * @returns Nulla.
   */
  deleteConnection(connectionId: string): Promise<void> {
    return this.client.request<void>('DELETE', connectionPath(connectionId));
  }

  /**
   * Elenca i repository visibili dal token di una connessione.
   *
   * @param connectionId Connessione.
   * @param query Testo da cercare nel nome.
   * @param page Pagina, da 1.
   * @returns Una pagina di repository.
   */
  listRepositories(connectionId: string, query = '', page = 1): Promise<RepositoryPage> {
    const params = new URLSearchParams({ query, page: String(page) });
    return this.client.request<RepositoryPage>(
      'GET',
      `${connectionPath(connectionId)}/repositories?${params.toString()}`,
    );
  }

  /**
   * Elenca i progetti Google Cloud visibili dalla credenziale di una
   * connessione Google (ADR 0011 §5).
   *
   * @param connectionId Connessione Google.
   * @param query Testo da cercare in identificativo e nome; vuoto = tutti.
   * @returns I progetti, al massimo 200.
   * @throws ApiError 403 se la connessione non è mia o non esiste, 409
   *   `provider_mismatch` se non è Google.
   */
  async listGoogleProjects(connectionId: string, query = ''): Promise<GoogleProject[]> {
    const params = new URLSearchParams({ query });
    const list = await this.client.request<GoogleProjectList>(
      'GET',
      `${connectionPath(connectionId)}/google/projects?${params.toString()}`,
    );
    return list.projects;
  }

  /**
   * Crea un progetto Google Cloud con la credenziale di una connessione Google.
   *
   * L'identificativo è globale su tutto Google. Se Google non finisce entro il
   * tempo del backend il progetto torna con `state: 'PENDING'`: è creato
   * comunque, e compare nell'elenco dei progetti.
   *
   * @param connectionId Connessione Google.
   * @param projectId Identificativo, forma `^[a-z][a-z0-9-]{4,28}[a-z0-9]$`.
   * @param name Nome leggibile, 4-30 caratteri; omesso, lo sceglie Google.
   * @returns Il progetto creato.
   * @throws ApiError 409 `google_project_exists` se l'identificativo è già preso,
   *   409 `google_project_refused` se Google rifiuta (il motivo sta nel
   *   messaggio), 409 `provider_mismatch`, `google_unreachable`,
   *   `google_credentials_invalid`, 422 per un identificativo malformato, 403 se
   *   la connessione non è mia.
   */
  createGoogleProject(connectionId: string, projectId: string, name?: string): Promise<GoogleProject> {
    const body = name === undefined || name === '' ? { project_id: projectId } : { project_id: projectId, name };
    return this.client.request<GoogleProject>('POST', `${connectionPath(connectionId)}/google/projects`, body);
  }

  /**
   * Legge la panoramica di un progetto Google Cloud: API attive, chiavi API
   * (senza le loro stringhe) e accesso degli utenti. Una sezione che Google
   * rifiuta ha il suo `status`, e non fa fallire le altre.
   *
   * @param connectionId Connessione Google.
   * @param gcpProject Identificativo del progetto Google Cloud.
   * @returns La panoramica.
   * @throws ApiError 403 se la connessione non è mia, 409 `provider_mismatch` se
   *   non è Google, 422 se l'identificativo è malformato, 404 se la credenziale
   *   non vede il progetto.
   */
  getGoogleProjectOverview(connectionId: string, gcpProject: string): Promise<GoogleProjectOverview> {
    return this.client.request<GoogleProjectOverview>(
      'GET',
      `${connectionPath(connectionId)}/google/projects/${encodeURIComponent(gcpProject)}`,
    );
  }

  /**
   * Legge richieste ed errori per API e la fatturazione di un progetto Google
   * Cloud (ADR 0012 §8).
   *
   * @param connectionId Connessione Google.
   * @param gcpProject Progetto Google Cloud.
   * @param days Giorni della finestra, da 1 a 90.
   * @returns I consumi, con lo stato di ciascuna sezione.
   */
  getGoogleUsage(connectionId: string, gcpProject: string, days = 30): Promise<GoogleUsage> {
    return this.client.request<GoogleUsage>(
      'GET',
      `${connectionPath(connectionId)}/google/projects/${encodeURIComponent(gcpProject)}/usage?days=${String(days)}`,
    );
  }

  /**
   * Legge il progetto Google gestito di un progetto institor (ADR 0012 §5).
   *
   * @param projectId Progetto institor.
   * @returns Il progetto gestito, oppure `null` se non esiste.
   */
  async getManagedGoogle(projectId: string): Promise<ManagedGoogleProject | null> {
    const response = await this.client.request<ManagedGoogleResponse>('GET', managedPath(projectId));
    return response.managed;
  }

  /**
   * Avvia o riprende la preparazione del progetto Google gestito.
   *
   * @param projectId Progetto institor.
   * @returns Lo stato dopo l'avvio (di solito `provisioning`).
   * @throws ApiError 409 `platform_google_missing` senza account di piattaforma,
   *   409 `project_has_google` se il progetto ha già un collegamento Google suo.
   */
  async prepareManagedGoogle(projectId: string): Promise<ManagedGoogleProject | null> {
    const response = await this.client.request<ManagedGoogleResponse>('POST', managedPath(projectId));
    return response.managed;
  }

  /**
   * Porta il progetto su un account Google dell'utente (ADR 0012 §6).
   *
   * @param projectId Progetto institor.
   * @param request Connessione di destinazione, modo e progetto nuovo.
   * @returns L'esito: Proprietario concesso, fatturazione spostata, progetto usato.
   * @throws ApiError 409 `managed_not_ready`, `google_project_exists`,
   *   `google_project_refused`; 403 se la connessione non è mia.
   */
  migrateManagedGoogle(projectId: string, request: MigrationRequest): Promise<MigrationResult> {
    return this.client.request<MigrationResult>('POST', `${managedPath(projectId)}/migrate`, request);
  }

  /**
   * Legge il repository GitHub gestito di un progetto (ADR 0014 §5).
   *
   * @param projectId Progetto institor.
   * @returns Il repository gestito, oppure `null` se non esiste.
   */
  async getManagedGithub(projectId: string): Promise<ManagedGithubRepository | null> {
    const response = await this.client.request<ManagedGithubResponse>('GET', githubManagedPath(projectId));
    return response.managed;
  }

  /**
   * Crea (o ricrea) il repository gestito e lo collega al progetto.
   *
   * @param projectId Progetto institor.
   * @returns Lo stato: `ready`, o `failed` con il motivo di GitHub.
   * @throws ApiError 409 `platform_github_missing` o `project_has_github`.
   */
  async createManagedGithub(projectId: string): Promise<ManagedGithubRepository | null> {
    const response = await this.client.request<ManagedGithubResponse>('POST', githubManagedPath(projectId));
    return response.managed;
  }

  /**
   * Sposta il repository gestito su un account GitHub dell'utente (ADR 0014 §6).
   *
   * @param projectId Progetto institor.
   * @param request Connessione e account di destinazione.
   * @returns L'esito; `completed` falso finché GitHub aspetta l'accettazione.
   * @throws ApiError 409 `managed_not_ready`, `github_repository_refused`; 403 se la connessione non è mia.
   */
  transferManagedGithub(projectId: string, request: GithubTransferRequest): Promise<GithubTransferResult> {
    return this.client.request<GithubTransferResult>('POST', `${githubManagedPath(projectId)}/transfer`, request);
  }

  /**
   * Controlla se il trasferimento è stato accettato e, se sì, lo conclude.
   *
   * @param projectId Progetto institor.
   * @returns L'esito.
   * @throws ApiError 409 `managed_not_ready` senza trasferimento in attesa.
   */
  checkManagedGithubTransfer(projectId: string): Promise<GithubTransferResult> {
    return this.client.request<GithubTransferResult>('POST', `${githubManagedPath(projectId)}/transfer/check`);
  }

  /**
   * Avvia il flusso per dispositivo di GitHub.
   *
   * Da confermare: ADR 0008 non fissa il corpo; si mandano proprietario e nome
   * della connessione che nascerà.
   *
   * @param owner Proprietario della connessione.
   * @param label Nome della connessione.
   * @returns Codice da digitare e pagina di verifica.
   */
  startDeviceFlow(owner: ConnectionOwnerRequest, label: string): Promise<DeviceFlowStart> {
    return this.client.request<DeviceFlowStart>('POST', `${BASE}/github/device`, {
      owner,
      label,
    });
  }

  /**
   * Chiede l'esito del flusso per dispositivo.
   *
   * @param flowId Flusso avviato.
   * @returns In attesa, completato con la connessione, scaduto o negato.
   */
  pollDeviceFlow(flowId: string): Promise<DeviceFlowPoll> {
    return this.client.request<DeviceFlowPoll>(
      'POST',
      `${BASE}/github/device/${encodeURIComponent(flowId)}/poll`,
    );
  }

  /**
   * Elenca i collegamenti di un progetto.
   *
   * @param projectId Progetto.
   * @returns I collegamenti.
   */
  listBindings(projectId: string): Promise<ToolBinding[]> {
    return this.client.request<ToolBinding[]>(
      'GET',
      `${BASE}/projects/${encodeURIComponent(projectId)}/bindings`,
    );
  }

  /**
   * Collega una connessione a un progetto.
   *
   * @param projectId Progetto.
   * @param binding Connessione, repository, accesso e regole.
   * @returns Il collegamento creato.
   */
  createBinding(projectId: string, binding: NewBinding): Promise<ToolBinding> {
    return this.client.request<ToolBinding>(
      'POST',
      `${BASE}/projects/${encodeURIComponent(projectId)}/bindings`,
      binding,
    );
  }

  /**
   * Cambia accesso o regole di un collegamento.
   *
   * @param bindingId Collegamento.
   * @param changes Campi da cambiare.
   * @returns Il collegamento aggiornato.
   */
  updateBinding(bindingId: string, changes: BindingChanges): Promise<ToolBinding> {
    return this.client.request<ToolBinding>(
      'PATCH',
      `${BASE}/bindings/${encodeURIComponent(bindingId)}`,
      changes,
    );
  }

  /**
   * Toglie un collegamento.
   *
   * @param bindingId Collegamento.
   * @returns Nulla.
   */
  deleteBinding(bindingId: string): Promise<void> {
    return this.client.request<void>('DELETE', `${BASE}/bindings/${encodeURIComponent(bindingId)}`);
  }
}
